import { SelectedBadge } from "./selected-badge";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type SelectionBadgesProps = {
  /** Array of selected option objects */
  selectedOptions: MultiSelectOptionT[];
  /** Number of badges that can be displayed */
  visibleCount: number;
  /** Placeholder text shown when no options are selected */
  placeholder: string;
  /** Whether the entire component is disabled */
  disabled: boolean;
  /** Callback when a badge remove button is clicked */
  onRemove: (value: string) => void;
  /** Ref for the container element (used for width calculations) */
  containerRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Container for displaying selected option badges
 *
 * Manages the display of visible badges, overflow indicator (+N),
 * and placeholder text. Handles dynamic badge visibility based
 * on available space.
 */
export function SelectionBadges({
  selectedOptions,
  visibleCount,
  placeholder,
  disabled,
  onRemove,
  containerRef,
}: SelectionBadgesProps) {
  return (
    <div
      ref={containerRef}
      className="flex h-full flex-1 items-center gap-1 overflow-hidden"
    >
      {selectedOptions.length > 0 ? (
        <>
          {/* Visible selected badges */}
          {selectedOptions.slice(0, visibleCount).map((option) => (
            <SelectedBadge
              key={option.value}
              option={option}
              disabled={disabled}
              onRemove={onRemove}
            />
          ))}

          {/* Overflow indicator badge (+N) */}
          {selectedOptions.length > visibleCount && (
            <div className="inline-flex h-full shrink-0 items-center rounded-[8px] bg-[var(--c-accent-bg)] px-2 py-0 text-xs leading-none font-normal text-[var(--c-accent)]">
              +{selectedOptions.length - visibleCount}
            </div>
          )}
        </>
      ) : (
        /* Placeholder when no options are selected */
        <span className="flex h-full items-center leading-none">
          {placeholder}
        </span>
      )}
    </div>
  );
}
